import CategoryScroller from 'features/Categories/components/CategoryScroller';
import SubCategoryDrawer from 'features/Categories/components/SubCategoryDrawer';
import compose from 'recompose/compose';
import DialogContainer from 'features/Dialog';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import FeaturedImage from 'features/Categories/components/FeaturedImage';
import has from 'lodash.has';
import isEqual from 'lodash.isequal';
import KioskLog from 'models/KioskLog';
import ProductFilters from 'models/ProductFilters';
import { CategoriesCardDisplay } from 'features/Categories';
import config from 'shared/config';
import React from 'react';
import toJS from 'shared/components/toJS';
import { actions as categoriesActions } from 'redux/domain/Categories';
import { actions as subCategoriesActions } from 'redux/domain/SubCategories';
import { actions as productFilterActions } from 'redux/domain/ProductFilter';
import { actions as productSearchActions } from 'redux/domain/ProductSearch';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FilterContainer } from 'features/ProductFilter';
import { getCancelTokenSource, cancelRequest } from 'api/Helpers';
import { PRODUCTS } from 'redux/domain/ProductSearch/constants';
import { PropTypes } from 'prop-types';
import { ProductsDisplay } from 'features/Product';
import { SUB_CATEGORIES } from 'redux/domain/SubCategories/constants';
import ParentCategoryBar from '../components/ParentCategoryBar';
import styles from '../styles.css';
import withRouter from '../../../shared/utils/WithRouter';

const topCategoryId = null;

/**
 * Given a categoryId this container will fetch a list of any available subcategories or
 * products. In eSTORIS a category may have additional subcategories, products, or both.
 * If this category has both a list of subcategories and products only subcategories will be displayed.
 */
class CategoriesContainer extends React.Component {
	constructor() {
		super();

		this._currentScrollTop = 0;
		this.cancelTokenProduct = getCancelTokenSource();
		this.cancelTokenSubcat = getCancelTokenSource();
		this.cancelTokenTopcat = getCancelTokenSource();
		this.handleScroll = this.handleScroll.bind(this);
	}

	componentDidMount() {
		const {
			id,
			filters,
			mostRecentCat,
			receiveFilters,
			toggleProductsDisplay,
			updateMostRecentCat,
			updateScrollPosition,
		} = this.props;
		// start with clean filters
		const cleanFilters = new ProductFilters();

		// skip usual requests if doing a search
		if (id === 'search') {
			// Temporary fetch. The last search is stored in the store and keyed off the search term.
			// Unfortunately the filter parameters are not considered. This means that our "cache" may
			// no longer hold what we want because while the search term hasn't changed the search results
			// may have been filtered by other parameters and no longer holds all the products associated with
			// the search term.
			if (mostRecentCat !== filters.term) {
				// If we're on the search page, keep the term but reset all other filters.
				cleanFilters.term = filters.term;
				this.getFilteredProductsAsync(id, cleanFilters, true);
				receiveFilters(cleanFilters);
				updateScrollPosition(0);
			}
			// product search. toggle products display on
			toggleProductsDisplay(true);
		} else if (id === topCategoryId) {
			toggleProductsDisplay(false);
			this.getTopCategoriesAsync();
			receiveFilters(cleanFilters);
		} else if (mostRecentCat !== id) {
			toggleProductsDisplay(false);
			// fetch products and categories
			this.getFilteredProductsAsync(id, cleanFilters, true);
			this.getSubCategoriesAsync(id);
			receiveFilters(cleanFilters);
			updateScrollPosition(0);
		}

		updateMostRecentCat(id !== 'search' ? id : filters.term);
	}

	/**
	 * This is a nested route. If a user clicks on a sub category react-router will update it's
	 * state with the new category Id. However, this component will not rerender because
	 * the component has already mounted and therefore loadSubCategories will not be called to
	 * update the state. This tells us that we've received a new categoryid so please get the
	 * new categories and re-render if necessary.
	 */
	componentWillReceiveProps(nextProps) {
		const {
			categories,
			displayProducts,
			id,
			filters,
			receiveFilters,
			setMaxPriceText,
			setMinPriceText,
			toggleProductsDisplay,
			toggleProductsAndCategoriesDisplay,
			updateMostRecentCat,
			updateScrollPosition,
		} = this.props;
		if (
			nextProps.id !== id ||
			(nextProps.id === 'search' && nextProps.filters.term !== filters.term)
		) {
			// Either the id changed, or we are on the search page and the term changed.
			// clear filters and reload all data
			const newFilters = new ProductFilters();
			if (nextProps.id === 'search') {
				// If we are on the search page, keep the term but reset all other filters.
				newFilters.term = nextProps.filters.term;
			}
			receiveFilters(newFilters);
			setMaxPriceText('');
			setMinPriceText('');
			this.initComponent(nextProps.id, newFilters);
			updateScrollPosition(0);
			updateMostRecentCat(nextProps.id !== 'search' ? nextProps.id : nextProps.filters.term);
		} else if (!isEqual(nextProps.filters, filters) && nextProps.id !== topCategoryId) {
			// filter changed. just refetch products
			this.getFilteredProductsAsync(nextProps.id, nextProps.filters, false);
		}
		// before doing an update check if we need to swap between products and subcategories
		const subCategories =
			nextProps.id === topCategoryId
				? categories
				: this.getItemsFromStore(id, SUB_CATEGORIES, nextProps);
		let products;
		if (nextProps.id !== 'search') {
			// get products by category
			products = this.getItemsFromStore(nextProps.id, PRODUCTS, nextProps);
		} else {
			// get products by search term
			products = this.getItemsFromStore(nextProps.filters.term, PRODUCTS, nextProps);
		}

		if (subCategories.length > 0 && products.length > 0) {
			toggleProductsAndCategoriesDisplay(true);
		} else if (subCategories.length > 0 && displayProducts) {
			toggleProductsDisplay(false);
			toggleProductsAndCategoriesDisplay(false);
		} else if (products.length > 0 && !displayProducts) {
			toggleProductsDisplay(true);
			toggleProductsAndCategoriesDisplay(false);
		}
	}

	componentWillUpdate(nextProps) {
		const { fetchErrorProduct, fetchErrorSubcategories, fetchErrorTopCategories } = nextProps;
		if (fetchErrorSubcategories || fetchErrorProduct || fetchErrorTopCategories) {
			let errorText = '';
			if (
				(fetchErrorSubcategories && fetchErrorProduct) ||
				(fetchErrorSubcategories && fetchErrorTopCategories) ||
				(fetchErrorProduct && fetchErrorTopCategories)
			) {
				errorText = 'multiple items';
			} else if (fetchErrorSubcategories) {
				errorText = 'the subcategories';
			} else if (fetchErrorProduct) {
				errorText = 'the product';
			} else {
				errorText = 'the top-categories';
			}
			const log = new KioskLog(
				new Error('Fetch Error in CategoriesContainer'),
				`Error fetching ${errorText}. - ID: ${nextProps.id}`,
			);
			log.postMessage();

			// checking as well if coming from the category page in case of reloading the page or coming from
			// the errorPage, nextProps won't tell us anything useful and will not load the categories page again.
			nextProps.updateCategoryFetchError(undefined);
			nextProps.updateProductFetchError(undefined);
			nextProps.updateSubCatFetchError(undefined);
			this.props.router.navigate('/errorPage', { 
				state: { 
					text: `We couldn't load ${errorText} to the Kiosk.`,
					id: `Given ID of ${nextProps.id}`, 
				} 
			});
		}
	}

	componentWillUnmount() {
		// if a request is in progress stop it.
		cancelRequest(this.cancelTokenProduct);
		cancelRequest(this.cancelTokenSubcat);
		cancelRequest(this.cancelTokenTopcat);

		// Update scroll position in store so we know where to scroll to when remounting the CategoriesContainer
		this.props.updateScrollPosition(this._currentScrollTop);
	}

	getFilteredProductsAsync(categoryId, filterOptions, loadFilterableOptions) {
		const { fetchingProductCounter, getFilteredProducts } = this.props;

		// Fetch is in progress. Cancel it because the filter parameters have changed.
		if (fetchingProductCounter > 0) {
			cancelRequest(this.cancelTokenProduct);
			this.cancelTokenProduct = getCancelTokenSource();
		}

		const reqParams = Object.assign(new ProductFilters(), filterOptions);
		if (categoryId !== 'search') {
			reqParams.categoryId = categoryId;
		}
		reqParams.loadFilterableOptions = loadFilterableOptions;
		getFilteredProducts(this.cancelTokenProduct.token, reqParams);
	}

	/**
	 * Retrieve subcategories assocated with this category.
	 * @param {number} categoryId - key to the subcategories row.
	 */
	getSubCategoriesAsync(categoryId) {
		const { getSubCategories } = this.props;
		getSubCategories(this.cancelTokenSubcat.token, categoryId);
	}

	/**
	 * Retrieve top-level categories
	 */
	getTopCategoriesAsync() {
		const { getTopCategories } = this.props;
		getTopCategories(this.cancelTokenTopcat.token);
	}

	/**
	 * Helper method to return the current page from the store.
	 */
	getCurrentPage() {
		const { filters, id, products } = this.props;
		const key = id === 'search' ? filters.term : id;
		return has(products, key) ? products[key].resultHeader.page : 0;
	}

	/**
	 * Return a list of products or subCategories associated with this category Id from the store
	 * if available.
	 * @param {number} key
	 * @param {string} type - either "products" or subCategories
	 * @param {React props} propsToCheck - optional props object that can be passed in. For example from
	 * componentWillReceiveProps we may be interested in nextProps instead of the current props.
	 * @returns An array of products or subCategories or an empty array if they have not be fetched
	 * yet or this sub category has no subcategories or products associated with it.
	 */
	getItemsFromStore(key, type, propsToCheck = undefined) {
		const prefetchedItems = propsToCheck ? propsToCheck[type] : this.props[type];
		return has(prefetchedItems, key, type) ? prefetchedItems[key][type] : [];
	}

	/**
	 * Handle the scroll event. When the user reaches the bottom of the page and more
	 * products are available go fetch them.
	 */
	handleScroll(event) {
		this._currentScrollTop = event.scrollTop;
		// events can fire very quickly in succession. Don't bother cancelling the previous request
		// and refetching since the search criteria has not changed on scroll.
		const { fetchingProductCounter, filters, id } = this.props;
		if (fetchingProductCounter > 0) return;
		// scrolled 40% of the page
		if (event.top > 0.4 && this.hasNext()) {
			const params = filters;
			const newParams = Object.assign(new ProductFilters(), params, {
				page: this.getCurrentPage() + 1,
			});
			this.getFilteredProductsAsync(id, newParams, false);
		}
	}

	/**
	 * Helper method to return whether there are more product pages available.
	 */
	hasNext() {
		const { filters, id, products } = this.props;
		const key = id === 'search' ? filters.term : id;
		return has(products, key) ? products[key].resultHeader.next : false;
	}

	/**
	 * Handle any needed componenent initialization.
	 * @param {number} categoryId
	 */
	initComponent(categoryId, filter) {
		if (categoryId === 'search') {
			this.getFilteredProductsAsync(categoryId, filter, true);
		} else if (categoryId === topCategoryId) {
			this.getTopCategoriesAsync();
		} else {
			this.getFilteredProductsAsync(categoryId, filter, true);
			this.getSubCategoriesAsync(categoryId);
		}
	}

	render() {
		const {
			categories,
			displayProducts,
			displayProductsAndCategories,
			fetchingProductCounter,
			filters,
			id,
			isFetchingSubcategories,
			isFetchingTopCategories,
			scrollPosition,
			topFeaturedImages,
		} = this.props;
		const subCategories =
			id === topCategoryId ? categories : this.getItemsFromStore(id, SUB_CATEGORIES);
		let products;
		if (id !== 'search') {
			// get products by category
			products = this.getItemsFromStore(id, PRODUCTS);
		} else {
			// get products by search term
			products = this.getItemsFromStore(filters.term, PRODUCTS);
		}
		const isFetchingProduct = fetchingProductCounter > 0;
		const fetching = isFetchingProduct || isFetchingSubcategories || isFetchingTopCategories;
		const displayFilterMessage = !fetching && products.length === 0;
		const thisCat = this.props.subCategories[id];
		const featuredImages = thisCat ? thisCat.featuredImages : [];
		return (
			<div>
				<ErrorBoundary>
					<ParentCategoryBar id={id} />
				</ErrorBoundary>
				<div>
					<ErrorBoundary>
						{displayProductsAndCategories ? (
							<div>
								<FilterContainer
									filtersOn
									displayFilterMessage={displayFilterMessage}
									catIdTerm={id === 'search' ? filters.term : id}
									displayProductsAndCategories={displayProductsAndCategories}
								/>
								<ProductsDisplay
									categoryId={id}
									products={products}
									isFetching={isFetchingProduct}
									scrollHandler={this.handleScroll}
									initialScrollPosition={scrollPosition}
									filters={filters}
									displayProductsAndCategories={displayProductsAndCategories}
								/>
								<SubCategoryDrawer
									categories={subCategories}
									isFetching={isFetchingSubcategories || isFetchingTopCategories}
								/>
							</div>
						) : (
							<div>
								{displayProducts ? (
									<div>
										<FilterContainer
											filtersOn
											displayFilterMessage={displayFilterMessage}
											catIdTerm={id === 'search' ? filters.term : id}
										/>
										<ProductsDisplay
											categoryId={id}
											products={products}
											isFetching={isFetchingProduct}
											scrollHandler={this.handleScroll}
											initialScrollPosition={scrollPosition}
											filters={filters}
											displayProductsAndCategories={displayProductsAndCategories}
										/>
										{displayFilterMessage ? (
											<div className={styles['page-message']}>
												<h2 className={styles['page-message-bold']}>
													We&apos;re Sorry. No products were found for your search.
												</h2>
												<div className={styles['page-message-italics']}>
													Please refine or contact a store associate for assistance.
												</div>
												<h2 className={styles['page-message-uppercase']}>Thank you!</h2>
											</div>
										) : null}
									</div>
								) : (
									<div>
										{config.GENERAL.CategoryCardView ? (
											<CategoriesCardDisplay
												categories={subCategories}
												isFetching={isFetchingSubcategories || isFetchingTopCategories}
												scrollHandler={this.handleScroll}
												initialScrollPosition={scrollPosition}
											/>
										) : (
											<div>
												<div
													className={styles['featured-container']}
													style={{
														/* The category scroller is 35%
												The featured slider should take up the remaining space
												minus the height of the toolbar (80) and parent category bar (40) */
														height: id === topCategoryId ? 'calc(65% - 80px)' : 'calc(65% - 120px)',
													}}
												>
													<FeaturedImage
														images={id === topCategoryId ? topFeaturedImages : featuredImages}
													/>
												</div>
												<CategoryScroller
													categories={subCategories}
													isFetching={isFetchingSubcategories || isFetchingTopCategories}
												/>
											</div>
										)}
										{!fetching && subCategories.length === 0 && id !== topCategoryId ? (
											<DialogContainer
												onClose={() => this.props.router.navigate('/category')}
												buttonLabel="OK"
												message="There are no products available in this category."
											/>
										) : null}
									</div>
								)}
							</div>
						)}
					</ErrorBoundary>
				</div>
			</div>
		);
	}
}

CategoriesContainer.propTypes = {
	id: PropTypes.string,
	// from actions
	getFilteredProducts: PropTypes.func.isRequired,
	getSubCategories: PropTypes.func.isRequired,
	getTopCategories: PropTypes.func.isRequired,
	setMaxPriceText: PropTypes.func.isRequired,
	setMinPriceText: PropTypes.func.isRequired,
	toggleProductsDisplay: PropTypes.func.isRequired,
	toggleProductsAndCategoriesDisplay: PropTypes.func.isRequired,
	// from state
	categories: PropTypes.arrayOf(
		PropTypes.shape({
			categoryId: PropTypes.number,
			name: PropTypes.string,
			PictureUrl: PropTypes.string,
			Description: PropTypes.string,
		}),
	).isRequired,
	displayProducts: PropTypes.bool.isRequired,
	displayProductsAndCategories: PropTypes.bool.isRequired,
	fetchingProductCounter: PropTypes.number.isRequired,
	filters: PropTypes.instanceOf(ProductFilters).isRequired,
	isFetchingSubcategories: PropTypes.bool.isRequired,
	isFetchingTopCategories: PropTypes.bool.isRequired,
	mostRecentCat: PropTypes.string,
	products: PropTypes.instanceOf(Object).isRequired,
	receiveFilters: PropTypes.func.isRequired,
	scrollPosition: PropTypes.number,
	subCategories: PropTypes.instanceOf(Object).isRequired,
	topFeaturedImages: PropTypes.arrayOf(PropTypes.object).isRequired,
	updateCategoryFetchError: PropTypes.func.isRequired,
	updateMostRecentCat: PropTypes.func.isRequired,
	updateProductFetchError: PropTypes.func.isRequired,
	updateScrollPosition: PropTypes.func.isRequired,
	updateSubCatFetchError: PropTypes.func.isRequired,
};

CategoriesContainer.defaultProps = {
	id: topCategoryId,
	mostRecentCat: null,
	scrollPosition: 0,
};

const mapStateToProps = state => {
	const categories = state.categories.get('categories');
	const displayProducts = state.subCategories.get('displayProducts');
	const displayProductsAndCategories = state.subCategories.get('displayProductsAndCategories');
	const fetchErrorProduct = state.productSearch.get('fetchError');
	const fetchErrorSubcategories = state.subCategories.get('fetchError');
	const fetchErrorTopCategories = state.categories.get('fetchError');
	const fetchingProductCounter = state.productSearch.get('fetchCounter');
	const filters = state.productFilter.get('filters');
	const isFetchingSubcategories = state.subCategories.get('isFetching');
	const isFetchingTopCategories = state.categories.get('isFetching');
	const products = state.productSearch.get('products');
	const subCategories = state.subCategories.get('subCategories');
	const topFeaturedImages = state.categories.get('topFeaturedImages');
	const mostRecentCat = state.subCategories.get('mostRecentCat');
	const scrollPosition = state.subCategories.get('scrollPosition');
	return {
		categories,
		displayProducts,
		displayProductsAndCategories,
		fetchErrorProduct,
		fetchErrorSubcategories,
		fetchErrorTopCategories,
		fetchingProductCounter,
		filters,
		isFetchingSubcategories,
		isFetchingTopCategories,
		products,
		subCategories,
		topFeaturedImages,
		mostRecentCat,
		scrollPosition,
	};
};

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			...categoriesActions,
			...subCategoriesActions,
			...productFilterActions,
			...productSearchActions,
			updateCategoryFetchError: categoriesActions.updateFetchError,
			updateProductFetchError: productSearchActions.updateFetchError,
			updateSubCatFetchError: subCategoriesActions.updateFetchError,
		},
		dispatch,
	);

// for testing purposes
export { CategoriesContainer as CategoriesContainerClass, mapStateToProps };

// connect returns a function that accepts a component
export default withRouter(compose(connect(mapStateToProps, mapDispatchToProps))(toJS(CategoriesContainer)));
